import React, { useState, useEffect } from "react";
import { Popover, Modal, Form, Input, Select, Button, Divider, Space, Spin, DatePicker, InputNumber, Tabs, Empty, Timeline, Typography, Tag, Card, Badge, Tooltip, Alert, message } from "antd";
import { EditOutlined, HistoryOutlined, CheckOutlined, CheckCircleOutlined, ClockCircleOutlined, CloseCircleOutlined, InfoCircleOutlined, FlagOutlined, MailOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import ReactMarkdown from "react-markdown";
import { TRANSACTION_TYPES, TRANSACTION_STATUS, TRANSACTION_STATUS_LABELS, TRANSACTION_TYPE_LABELS, TRANSACTION_STATUS_COLORS, DEFAULT_TRANSACTION_DETAILS, TRANSACTION_TYPE_TO_MODEL_MAP, MODEL_TO_TRANSACTION_TYPE_MAP, TRANSACTION_TYPE_SPECIFIC_STATUS, TRANSACTION_CHANGE_TYPES, TRANSACTION_MIGRATION_STATUS } from "../constants";
import { FLIGHT_STATUS_COLORS, FLIGHT_STATUS_LABELS } from "../constants/flightConstants";
import TransactionStatusTag from "./TransactionStatusTag";
import TransactionHistoryChain from "./TransactionHistoryChain";
import { flightOperationEventLogService, flightOperationTransactionService } from "../../../../api/enhancedApi";
import "./TransactionStyles.css";

const { TextArea } = Input;
const { TabPane } = Tabs;
const { Text, Title } = Typography;

dayjs.extend(utc);

// 事务状态选择器组件 - 类似 FlightStatusSelector 的交互方式
const TransactionStatusSelector = ({ form, fieldName = "status", transactionType, onFieldChange }) => {
    const currentStatus = Form.useWatch(fieldName, form);

    const handleStatusClick = (status) => {
        form.setFieldValue(fieldName, status);
        // 记录字段修改
        if (onFieldChange) {
            onFieldChange(fieldName, status);
        }
    };

    const handleClearStatus = () => {
        form.setFieldValue(fieldName, null);
        // 记录字段修改
        if (onFieldChange) {
            onFieldChange(fieldName, null);
        }
    };

    // 根据事务类型获取状态列表
    const getStatusesForType = () => {
        if (transactionType && TRANSACTION_TYPE_SPECIFIC_STATUS[transactionType]) {
            return Object.values(TRANSACTION_TYPE_SPECIFIC_STATUS[transactionType]);
        }
        // 默认使用通用状态
        return Object.values(TRANSACTION_STATUS);
    };

    const statusOrder = getStatusesForType();

    return (
        <div style={{ display: "flex", flexDirection: "row", gap: "8px", flexWrap: "wrap" }}>
            {statusOrder.map((status) => {
                const isActive = currentStatus === status;
                return (
                    <Button
                        key={status}
                        type={isActive ? "primary" : "default"}
                        ghost={isActive}
                        size="small"
                        onClick={() => handleStatusClick(status)}
                        style={{
                            textAlign: "center",
                            padding: "0 12px",
                            minWidth: "auto", // 让按钮宽度自适应内容
                            flex: "none", // 不拉伸
                            color: isActive ? TRANSACTION_STATUS_COLORS[status] : undefined,
                            borderColor: isActive ? TRANSACTION_STATUS_COLORS[status] : undefined,
                        }}
                    >
                        {isActive && <CheckOutlined />} {TRANSACTION_STATUS_LABELS[status]}
                    </Button>
                );
            })}
            {/* 添加清除按钮 */}
            <Button
                type={currentStatus === null ? "primary" : "default"}
                danger={currentStatus === null}
                ghost={currentStatus === null}
                size="small"
                onClick={handleClearStatus}
                style={{
                    textAlign: "center",
                    padding: "0 12px",
                    minWidth: "auto",
                    flex: "none",
                }}
                icon={<CloseCircleOutlined />}
            >
                清除状态
            </Button>
        </div>
    );
};

/**
 * 事务编辑弹出框组件
 * 重新设计：悬浮显示信息，点击按钮打开编辑Modal
 * @param {number} flightId - 航班ID
 * @param {string} transactionType - 事务类型
 * @param {Object} transaction - 传入的事务对象(如果已经有预加载数据)
 * @param {boolean} visible - 弹出框是否可见
 * @param {function} onVisibleChange - 可见性变更回调
 * @param {string} trigger - 触发方式
 * @param {string} placement - 弹出框位置
 * @param {React.ReactNode} children - 子元素
 * @param {function} onSuccess - 可选，事务操作成功时的回调函数，传递 flightId
 * @param {function} createTransaction - 创建事务的方法
 * @param {function} updateTransaction - 更新事务的方法
 * @param {function} fetchTransactions - 刷新事务的方法
 * @param {Object} flight - 航班对象
 */
const TransactionEditPopover = ({ flightId, transactionType, transaction = null, visible, onVisibleChange, trigger = "hover", placement = "right", children, onSuccess, createTransaction, updateTransaction, fetchTransactions, flight }) => {
    const [form] = Form.useForm();
    const [modalVisible, setModalVisible] = useState(false);
    // 移除activeTab状态，因为不再使用Tab布局
    const [formInitialValues, setFormInitialValues] = useState({});
    const [eventLogs, setEventLogs] = useState([]);
    const [eventLogsLoading, setEventLogsLoading] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    // 记录用户真正修改过的字段（包含值）- 统一使用修改跟踪
    const [modifiedFields, setModifiedFields] = useState({});

    // 当前事务（使用传入的transaction，但如果是REJECTED状态则视为null）
    const currentTransaction = transaction?.migration_status === TRANSACTION_MIGRATION_STATUS.REJECTED ? null : transaction;

    // 监听每个许可状态，保证tab标签实时刷新
    const departureLandingStatus = Form.useWatch("departureLandingStatus", form);
    const arrivalLandingStatus = Form.useWatch("arrivalLandingStatus", form);
    const overFlightStatus = Form.useWatch("overFlightStatus", form);

    // 初始化各tab状态显示所需的真实状态
    const [tabStatus, setTabStatus] = useState({
        departureLanding: undefined,
        arrivalLanding: undefined,
        overflight: undefined,
    });

    // 记录用户修改的字段及其值
    const recordFieldChange = (fieldName, value) => {
        setModifiedFields((prev) => ({
            ...prev,
            [fieldName]: value,
        }));
    };

    // 更新初始状态及状态变更监听
    useEffect(() => {
        if (currentTransaction?.details) {
            const details = currentTransaction.details;
            // 更新tab显示状态
            setTabStatus({
                departureLanding: details.departureLandingPermission?.status,
                arrivalLanding: details.arrivalLandingPermission?.status,
                overflight: details.overFlightPermission?.status,
            });
        } else if (!currentTransaction) {
            // 新增处理: 如果是创建新事务
            setTabStatus({
                departureLanding: null,
                arrivalLanding: null,
                overflight: null,
            });
        }
    }, [currentTransaction]);

    // 监听表单值变化，更新tab状态
    useEffect(() => {
        if (departureLandingStatus !== undefined && departureLandingStatus !== null) {
            setTabStatus((prev) => ({ ...prev, departureLanding: departureLandingStatus }));
        }
    }, [departureLandingStatus]);

    useEffect(() => {
        if (arrivalLandingStatus !== undefined && arrivalLandingStatus !== null) {
            setTabStatus((prev) => ({ ...prev, arrivalLanding: arrivalLandingStatus }));
        }
    }, [arrivalLandingStatus]);

    useEffect(() => {
        if (overFlightStatus !== undefined && overFlightStatus !== null) {
            setTabStatus((prev) => ({ ...prev, overflight: overFlightStatus }));
        }
    }, [overFlightStatus]);

    // 加载事件日志的方法
    const loadEventLogs = async () => {
        if (!currentTransaction) return;

        setEventLogsLoading(true);
        try {
            // 直接使用API服务获取事件日志
            const response = await flightOperationEventLogService.getFlightEventLogsByFlightId(flightId, {
                related_business_type: TRANSACTION_TYPE_TO_MODEL_MAP[transactionType] || transactionType,
                limit: 50,
                sort_by: "created_at",
                sort_order: "desc",
            });

            if (response.data && Array.isArray(response.data.items)) {
                setEventLogs(response.data.items);
            } else {
                setEventLogs([]);
            }
        } catch (err) {
            console.error("获取事件日志失败:", err);
            setEventLogs([]);
        } finally {
            setEventLogsLoading(false);
        }
    };

    // 处理表单初始值
    useEffect(() => {
        let dataToSet;
        if (currentTransaction) {
            const initialData = {
                status: currentTransaction.status,
                remarks: currentTransaction.remarks || "",
                // Ensure details are spread, default to empty object if not present
                ...(currentTransaction.details || {}),
            };

            switch (transactionType) {
                case TRANSACTION_TYPES.PERMIT_REQUEST:
                    if (currentTransaction.details) {
                        const details = currentTransaction.details;
                        if (details.departureLandingPermission) {
                            initialData.departureLandingStatus = details.departureLandingPermission.status;
                            initialData.departureLandingReferenceNumber = details.departureLandingPermission.referenceNumber;
                            initialData.departureLandingManifestNumber = details.departureLandingPermission.manifestNumber;
                            initialData.departureLandingRemarks = details.departureLandingPermission.remarks;
                        }
                        if (details.arrivalLandingPermission) {
                            initialData.arrivalLandingStatus = details.arrivalLandingPermission.status;
                            initialData.arrivalLandingReferenceNumber = details.arrivalLandingPermission.referenceNumber;
                            initialData.arrivalLandingManifestNumber = details.arrivalLandingPermission.manifestNumber;
                            initialData.arrivalLandingRemarks = details.arrivalLandingPermission.remarks;
                        }
                        if (details.overFlightPermission) {
                            initialData.overFlightStatus = details.overFlightPermission.status;
                            initialData.overFlightReferenceNumber = details.overFlightPermission.referenceNumber;
                            initialData.overFlightStd = details.overFlightPermission.std ? dayjs(details.overFlightPermission.std) : null;
                            initialData.overFlightSta = details.overFlightPermission.sta ? dayjs(details.overFlightPermission.sta) : null;
                            initialData.overFlightDelayHours = details.overFlightPermission.delayHours;
                            initialData.overFlightRemarks = details.overFlightPermission.remarks;
                        }
                    }
                    break;
                case TRANSACTION_TYPES.FUEL_REQUEST:
                    if (currentTransaction.details) {
                        const details = currentTransaction.details;
                        initialData.orderNumber = details.orderNumber;
                        initialData.expiryDate = details.expiryDate ? dayjs(details.expiryDate) : null;
                        initialData.maxPayload = details.maxPayload || null;
                    }
                    break;
                case TRANSACTION_TYPES.CATERING_ORDER:
                    if (currentTransaction.details) {
                        const details = currentTransaction.details;
                        initialData.version = details.version;
                        initialData.crewNumber = details.crewNumber || 2;
                        initialData.orderNo = details.orderNo;
                    }
                    break;
                case TRANSACTION_TYPES.FLIGHT_BRIEF:
                    if (currentTransaction.details) {
                        const details = currentTransaction.details;
                        initialData.maxPayload = details.maxPayload || null;
                        initialData.version = details.version;
                    }
                    break;
                case TRANSACTION_TYPES.CREW_TRANSPORTATION:
                    if (currentTransaction.details) {
                        const details = currentTransaction.details;
                        initialData.route = details.route;
                        initialData.pickup_time = details.pickup_time ? dayjs(details.pickup_time) : null;
                        initialData.pax_number = details.pax_number;
                        initialData.version = details.version;
                        initialData.order_number = details.order_number;
                    }
                    break;
                case TRANSACTION_TYPES.HOTAC:
                    if (currentTransaction.details) {
                        const details = currentTransaction.details;
                        initialData.pax_number = details.pax_number;
                        initialData.version = details.version;
                        initialData.order_number = details.order_number;
                    }
                    break;
                default:
                    break;
            }
            dataToSet = initialData;
        } else {
            const defaultValues = {
                remarks: "",
            };
            switch (transactionType) {
                case TRANSACTION_TYPES.PERMIT_REQUEST:
                    break;
                case TRANSACTION_TYPES.FUEL_REQUEST:
                    defaultValues.orderNumber = "";
                    defaultValues.maxPayload = null;
                    break;
                case TRANSACTION_TYPES.CATERING_ORDER:
                    defaultValues.version = "V1";
                    defaultValues.crewNumber = 5;
                    defaultValues.orderNo = "";
                    break;
                case TRANSACTION_TYPES.FLIGHT_BRIEF:
                    defaultValues.maxPayload = null;
                    defaultValues.version = "V1";
                    break;
                case TRANSACTION_TYPES.CREW_TRANSPORTATION:
                    defaultValues.route = undefined;
                    defaultValues.pickup_time = null;
                    defaultValues.pax_number = 1;
                    defaultValues.version = "V1";
                    defaultValues.order_number = "";
                    break;
                case TRANSACTION_TYPES.HOTAC:
                    defaultValues.pax_number = 1;
                    defaultValues.version = "V1";
                    defaultValues.order_number = "";
                    break;
                default:
                    break;
            }
            dataToSet = defaultValues;
        }

        setFormInitialValues(dataToSet); // Set state for Form's initialValues prop
        form.setFieldsValue(dataToSet); // Directly set values on the current form instance
        setModifiedFields({}); // Reset modified fields after programmatic set
    }, [currentTransaction, transactionType, form]); // `form` is a key dependency here

    // 处理打开Modal
    const handleOpenModal = () => {
        form.resetFields(); // Explicitly reset form to its initial state (based on initialValues prop)
        setModifiedFields({}); // Clear tracked modifications
        setModalVisible(true);
        // 打开Modal时自动加载事件日志
        if (currentTransaction) {
            loadEventLogs();
        }
    };

    // 处理查看历史详情按钮点击
    const handleViewHistory = () => {
        setModalVisible(true);
        loadEventLogs();
    };

    // 表单提交处理 - 统一使用变更命令方式
    const handleSubmit = async () => {
        if (isSubmitting) return;

        try {
            setIsSubmitting(true);

            // 获取表单值
            const values = await form.validateFields();

            // 构建事务数据 - 只包含用户修改的字段
            const transactionData = {
                details: extractDetailsFromValues(values, transactionType, modifiedFields),
            };

            // 只有当status字段被用户明确修改时才包含
            if ("status" in modifiedFields) {
                transactionData.status = values.status;
            }

            // 只有当remarks字段被用户明确修改时才包含
            if ("remarks" in modifiedFields) {
                transactionData.remarks = values.remarks;
            }

            // 确定正确的变更类型
            const changeType = currentTransaction ? TRANSACTION_CHANGE_TYPES[transactionType]?.update : TRANSACTION_CHANGE_TYPES[transactionType]?.create;

            if (!changeType) {
                throw new Error(`不支持的事务类型: ${transactionType}`);
            }

            // 构建变更命令
            const command = {
                flight_operation_id: flightId,
                change_type: changeType,
                payload: transactionData,
                operator: {
                    type: "USER",
                    id: "current_user", // 这里应该使用实际的用户ID
                },
                trigger_source: "MONITOR_UI_SINGLE",
                // 只有当remarks实际存在且被修改时才包含
                ...("remarks" in modifiedFields ? { remarks: values.remarks } : {}),
            };

            // 如果是更新操作，需要添加事务ID
            if (currentTransaction && changeType.includes("UPDATE")) {
                const transactionIdKey = getTransactionIdKey(transactionType);
                command.payload[transactionIdKey] = currentTransaction.id;
            }

            const response = await flightOperationTransactionService.createTransactionViaChange(command);

            // 正确处理 axios 响应结构 - 检查批量操作的详细结果
            if (response.data && response.data.success) {
                // 检查是否有失败的项目
                const responseData = response.data.data;
                if (responseData && responseData.failed_items && responseData.failed_items.length > 0) {
                    // 有失败项目，从失败项目中查找当前航班
                    const failedItem = responseData.failed_items.find((item) => item.flight_id === flightId);
                    if (failedItem) {
                        const errorMessage = failedItem.error || "操作失败";
                        console.error("事务操作失败:", errorMessage);
                        message.error(`事务操作失败: ${errorMessage}`);
                        return;
                    }
                }

                // 操作成功
                setModalVisible(false);
                onVisibleChange(false); // 关闭 Popover
                if (onSuccess && typeof onSuccess === "function") {
                    onSuccess(flightId); // 通知父组件成功，并传递 flightId
                }
            } else {
                const errorMessage = (response.data && response.data.message) || response.message || "操作失败";
                console.error("事务操作失败:", errorMessage);
                message.error(`事务操作失败: ${errorMessage}`);
            }
        } catch (err) {
            console.error("保存事务数据失败:", err);
        } finally {
            setIsSubmitting(false);
        }
    };

    // 获取事务ID字段名
    const getTransactionIdKey = (transactionType) => {
        const idKeyMap = {
            [TRANSACTION_TYPES.PERMIT_REQUEST]: "permit_request_id",
            [TRANSACTION_TYPES.FUEL_REQUEST]: "fuel_request_id",
            [TRANSACTION_TYPES.CATERING_ORDER]: "catering_order_id",
            [TRANSACTION_TYPES.FLIGHT_BRIEF]: "flight_brief_id",
            [TRANSACTION_TYPES.OCC_NOTE]: "occ_note_id",
            [TRANSACTION_TYPES.CREW_TRANSPORTATION]: "crew_transportation_id",
            [TRANSACTION_TYPES.HOTAC]: "hotac_id",
        };
        return idKeyMap[transactionType] || "transaction_id";
    };

    // 从表单值中提取details字段 - 只提取用户实际修改的字段
    const extractDetailsFromValues = (values, transactionType, modifiedFields) => {
        // 创建一个空对象用于存储最终结果
        const result = {};

        // 针对不同事务类型进行特殊处理
        switch (transactionType) {
            case TRANSACTION_TYPES.PERMIT_REQUEST:
                // 许可申请需要特殊处理嵌套结构
                const departureLandingPermission = {};
                const arrivalLandingPermission = {};
                const overFlightPermission = {};

                // 只包含用户实际修改的字段
                if ("departureLandingStatus" in modifiedFields) {
                    departureLandingPermission.status = modifiedFields.departureLandingStatus;
                }
                if ("departureLandingReferenceNumber" in modifiedFields) {
                    departureLandingPermission.referenceNumber = modifiedFields.departureLandingReferenceNumber;
                }
                if ("departureLandingManifestNumber" in modifiedFields) {
                    departureLandingPermission.manifestNumber = modifiedFields.departureLandingManifestNumber;
                }
                if ("departureLandingRemarks" in modifiedFields) {
                    departureLandingPermission.remarks = modifiedFields.departureLandingRemarks;
                }

                if ("arrivalLandingStatus" in modifiedFields) {
                    arrivalLandingPermission.status = modifiedFields.arrivalLandingStatus;
                }
                if ("arrivalLandingReferenceNumber" in modifiedFields) {
                    arrivalLandingPermission.referenceNumber = modifiedFields.arrivalLandingReferenceNumber;
                }
                if ("arrivalLandingManifestNumber" in modifiedFields) {
                    arrivalLandingPermission.manifestNumber = modifiedFields.arrivalLandingManifestNumber;
                }
                if ("arrivalLandingRemarks" in modifiedFields) {
                    arrivalLandingPermission.remarks = modifiedFields.arrivalLandingRemarks;
                }

                if ("overFlightStatus" in modifiedFields) {
                    overFlightPermission.status = modifiedFields.overFlightStatus;
                }
                if ("overFlightReferenceNumber" in modifiedFields) {
                    overFlightPermission.referenceNumber = modifiedFields.overFlightReferenceNumber;
                }
                if ("overFlightStd" in modifiedFields) {
                    overFlightPermission.std = modifiedFields.overFlightStd ? (typeof modifiedFields.overFlightStd === "string" ? modifiedFields.overFlightStd : modifiedFields.overFlightStd.toISOString()) : null;
                }
                if ("overFlightSta" in modifiedFields) {
                    overFlightPermission.sta = modifiedFields.overFlightSta ? (typeof modifiedFields.overFlightSta === "string" ? modifiedFields.overFlightSta : modifiedFields.overFlightSta.toISOString()) : null;
                }
                if ("overFlightDelayHours" in modifiedFields) {
                    overFlightPermission.delayHours = modifiedFields.overFlightDelayHours;
                }
                if ("overFlightRemarks" in modifiedFields) {
                    overFlightPermission.remarks = modifiedFields.overFlightRemarks;
                }

                // 只有当对象中有属性时，才添加到结果中
                if (Object.keys(departureLandingPermission).length > 0) {
                    result.departureLandingPermission = departureLandingPermission;
                }
                if (Object.keys(arrivalLandingPermission).length > 0) {
                    result.arrivalLandingPermission = arrivalLandingPermission;
                }
                if (Object.keys(overFlightPermission).length > 0) {
                    result.overFlightPermission = overFlightPermission;
                }

                return result;

            case TRANSACTION_TYPES.FUEL_REQUEST:
                // 处理燃油申请字段
                if ("orderNumber" in modifiedFields) {
                    result.orderNumber = modifiedFields.orderNumber;
                }
                if ("expiryDate" in modifiedFields) {
                    result.expiryDate = modifiedFields.expiryDate ? (typeof modifiedFields.expiryDate === "string" ? modifiedFields.expiryDate : modifiedFields.expiryDate.toISOString()) : null;
                }
                if ("maxPayload" in modifiedFields) {
                    result.maxPayload = modifiedFields.maxPayload;
                }
                return result;

            case TRANSACTION_TYPES.CATERING_ORDER:
                // 处理配餐订单字段
                if ("version" in modifiedFields) {
                    result.version = modifiedFields.version;
                }
                if ("crewNumber" in modifiedFields) {
                    result.crewNumber = modifiedFields.crewNumber;
                }
                if ("orderNo" in modifiedFields) {
                    result.orderNo = modifiedFields.orderNo;
                }
                return result;

            case TRANSACTION_TYPES.FLIGHT_BRIEF:
                // 处理飞行简报字段
                if ("maxPayload" in modifiedFields) {
                    result.maxPayload = modifiedFields.maxPayload;
                }
                if ("version" in modifiedFields) {
                    result.version = modifiedFields.version;
                }
                return result;

            case TRANSACTION_TYPES.CREW_TRANSPORTATION:
                if ("route" in modifiedFields) {
                    result.route = modifiedFields.route;
                }
                if ("pickup_time" in modifiedFields) {
                    // 直接使用用户输入的时间，不进行时区转换
                    if (modifiedFields.pickup_time) {
                        if (typeof modifiedFields.pickup_time === "string") {
                            result.pickup_time = modifiedFields.pickup_time;
                        } else if (modifiedFields.pickup_time.format) {
                            // 使用format方法获取本地时间字符串，不转换为UTC
                            result.pickup_time = modifiedFields.pickup_time.format("YYYY-MM-DD HH:mm:ss");
                        }
                    } else {
                        result.pickup_time = null;
                    }
                }
                if ("pax_number" in modifiedFields) {
                    result.pax_number = modifiedFields.pax_number;
                }
                if ("version" in modifiedFields) {
                    result.version = modifiedFields.version;
                }
                if ("order_number" in modifiedFields) {
                    result.order_number = modifiedFields.order_number;
                }
                return result;

            case TRANSACTION_TYPES.HOTAC:
                if ("pax_number" in modifiedFields) {
                    result.pax_number = modifiedFields.pax_number || 0;
                }
                if ("version" in modifiedFields) {
                    result.version = modifiedFields.version;
                }
                if ("order_number" in modifiedFields) {
                    result.order_number = modifiedFields.order_number;
                }
                return result;

            default:
                return {};
        }
    };

    // 渲染Popover内容 - 简要信息
    const renderPopoverContent = () => {
        if (!currentTransaction) {
            return (
                <div className="transaction-popover-empty">
                    <Text type="secondary">暂无{TRANSACTION_TYPE_LABELS[transactionType]}记录</Text>
                    <div style={{ marginTop: 12 }}>
                        <Button type="primary" size="small" icon={<EditOutlined />} onClick={handleOpenModal} className="transaction-action-button">
                            创建{TRANSACTION_TYPE_LABELS[transactionType]}
                        </Button>
                    </div>
                </div>
            );
        }

        // 渲染事务基本信息
        return (
            <div className="transaction-popover-content">
                {/* 特殊处理许可申请的三种类型 */}
                {transactionType === TRANSACTION_TYPES.PERMIT_REQUEST && currentTransaction.details && (
                    <div className="transaction-permissions">
                        <div className="transaction-permission-item">
                            <Text strong style={{ fontSize: "12px" }}>
                                始发机场落地许可
                            </Text>
                            <TransactionStatusTag status={currentTransaction.details.departureLandingPermission?.status} textOnly showIcon size="small" style={{ marginLeft: 8 }} />
                            <div className="permission-detail-list">
                                {currentTransaction.details.departureLandingPermission?.referenceNumber && (
                                    <div className="permission-detail">
                                        <Text type="secondary" style={{ fontSize: "12px" }}>
                                            参考号:
                                        </Text>
                                        <Text style={{ fontSize: "12px" }}>{currentTransaction.details.departureLandingPermission.referenceNumber}</Text>
                                    </div>
                                )}
                                {currentTransaction.details.departureLandingPermission?.manifestNumber && (
                                    <div className="permission-detail">
                                        <Text type="secondary" style={{ fontSize: "12px" }}>
                                            舱单号:
                                        </Text>
                                        <Text style={{ fontSize: "12px" }}>{currentTransaction.details.departureLandingPermission.manifestNumber}</Text>
                                    </div>
                                )}
                                {currentTransaction.details.departureLandingPermission?.remarks && (
                                    <div className="permission-detail">
                                        <Text type="secondary" style={{ fontSize: "12px" }}>
                                            备注:
                                        </Text>
                                        <Text style={{ fontSize: "12px" }}>{currentTransaction.details.departureLandingPermission.remarks}</Text>
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className="transaction-permission-item">
                            <Text strong style={{ fontSize: "12px" }}>
                                飞越许可
                            </Text>
                            <TransactionStatusTag status={currentTransaction.details.overFlightPermission?.status} textOnly showIcon size="small" style={{ marginLeft: 8 }} />
                            <div className="permission-detail-list">
                                {currentTransaction.details.overFlightPermission?.referenceNumber && (
                                    <div className="permission-detail">
                                        <Text type="secondary" style={{ fontSize: "12px" }}>
                                            参考号:
                                        </Text>
                                        <Text style={{ fontSize: "12px" }}>{currentTransaction.details.overFlightPermission.referenceNumber}</Text>
                                    </div>
                                )}
                                {currentTransaction.details.overFlightPermission?.std && (
                                    <div className="permission-detail">
                                        <Text type="secondary" style={{ fontSize: "12px" }}>
                                            标准起飞:
                                        </Text>
                                        <Text style={{ fontSize: "12px" }}>{dayjs(currentTransaction.details.overFlightPermission.std).format("YYYY-MM-DD HH:mm")}</Text>
                                    </div>
                                )}
                                {currentTransaction.details.overFlightPermission?.sta && (
                                    <div className="permission-detail">
                                        <Text type="secondary" style={{ fontSize: "12px" }}>
                                            标准到达:
                                        </Text>
                                        <Text style={{ fontSize: "12px" }}>{dayjs(currentTransaction.details.overFlightPermission.sta).format("YYYY-MM-DD HH:mm")}</Text>
                                    </div>
                                )}
                                {currentTransaction.details.overFlightPermission?.delayHours !== undefined && currentTransaction.details.overFlightPermission?.delayHours !== null && (
                                    <div className="permission-detail">
                                        <Text type="secondary" style={{ fontSize: "12px" }}>
                                            延误小时:
                                        </Text>
                                        <Text style={{ fontSize: "12px" }}>{currentTransaction.details.overFlightPermission.delayHours}</Text>
                                    </div>
                                )}
                                {currentTransaction.details.overFlightPermission?.remarks && (
                                    <div className="permission-detail">
                                        <Text type="secondary" style={{ fontSize: "12px" }}>
                                            备注:
                                        </Text>
                                        <Text style={{ fontSize: "12px" }}>{currentTransaction.details.overFlightPermission.remarks}</Text>
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className="transaction-permission-item">
                            <Text strong style={{ fontSize: "12px" }}>
                                目的机场落地许可
                            </Text>
                            <TransactionStatusTag status={currentTransaction.details.arrivalLandingPermission?.status} textOnly showIcon size="small" style={{ marginLeft: 8 }} />
                            <div className="permission-detail-list">
                                {currentTransaction.details.arrivalLandingPermission?.referenceNumber && (
                                    <div className="permission-detail">
                                        <Text type="secondary" style={{ fontSize: "12px" }}>
                                            参考号:
                                        </Text>
                                        <Text style={{ fontSize: "12px" }}>{currentTransaction.details.arrivalLandingPermission.referenceNumber}</Text>
                                    </div>
                                )}
                                {currentTransaction.details.arrivalLandingPermission?.manifestNumber && (
                                    <div className="permission-detail">
                                        <Text type="secondary" style={{ fontSize: "12px" }}>
                                            舱单号:
                                        </Text>
                                        <Text style={{ fontSize: "12px" }}>{currentTransaction.details.arrivalLandingPermission.manifestNumber}</Text>
                                    </div>
                                )}
                                {currentTransaction.details.arrivalLandingPermission?.remarks && (
                                    <div className="permission-detail">
                                        <Text type="secondary" style={{ fontSize: "12px" }}>
                                            备注:
                                        </Text>
                                        <Text style={{ fontSize: "12px" }}>{currentTransaction.details.arrivalLandingPermission.remarks}</Text>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {/* 燃油申请详情 */}
                {transactionType === TRANSACTION_TYPES.FUEL_REQUEST && currentTransaction.details && (
                    <div className="transaction-details">
                        {currentTransaction.details.orderNumber && (
                            <div className="transaction-detail-item">
                                <Text type="secondary" style={{ fontSize: "12px" }}>
                                    订单编号:{" "}
                                </Text>
                                <Text style={{ fontSize: "12px" }}>{currentTransaction.details.orderNumber}</Text>
                            </div>
                        )}
                        {currentTransaction.details.expiryDate && (
                            <div className="transaction-detail-item">
                                <Text type="secondary" style={{ fontSize: "12px" }}>
                                    有效期:{" "}
                                </Text>
                                <Text style={{ fontSize: "12px" }}>{dayjs(currentTransaction.details.expiryDate).format("YYYY-MM-DD")}</Text>
                            </div>
                        )}
                    </div>
                )}

                {/* 配餐订单详情 */}
                {transactionType === TRANSACTION_TYPES.CATERING_ORDER && currentTransaction.details && (
                    <div className="transaction-details">
                        {currentTransaction.details.orderNo && (
                            <div className="transaction-detail-item">
                                <Text type="secondary" style={{ fontSize: "12px" }}>
                                    订单编号:{" "}
                                </Text>
                                <Text style={{ fontSize: "12px" }}>{currentTransaction.details.orderNo}</Text>
                            </div>
                        )}
                        {currentTransaction.details.version && (
                            <div className="transaction-detail-item">
                                <Text type="secondary" style={{ fontSize: "12px" }}>
                                    版本:{" "}
                                </Text>
                                <Text style={{ fontSize: "12px" }}>{currentTransaction.details.version}</Text>
                            </div>
                        )}
                        {currentTransaction.details.crewNumber !== null && (
                            <div className="transaction-detail-item">
                                <Text type="secondary" style={{ fontSize: "12px" }}>
                                    机组人数:{" "}
                                </Text>
                                <Text style={{ fontSize: "12px" }}>{currentTransaction.details.crewNumber} 人</Text>
                            </div>
                        )}
                    </div>
                )}

                {/* 飞行简报详情 */}
                {transactionType === TRANSACTION_TYPES.FLIGHT_BRIEF && currentTransaction.details && (
                    <div className="transaction-details">
                        {currentTransaction.details.version && (
                            <div className="transaction-detail-item">
                                <Text type="secondary" style={{ fontSize: "12px" }}>
                                    版本:{" "}
                                </Text>
                                <Text style={{ fontSize: "12px" }}>{currentTransaction.details.version}</Text>
                            </div>
                        )}
                        {currentTransaction.details.maxPayload !== null && (
                            <div className="transaction-detail-item">
                                <Text type="secondary" style={{ fontSize: "12px" }}>
                                    最大载荷:{" "}
                                </Text>
                                <Text style={{ fontSize: "12px" }}>{currentTransaction.details.maxPayload} 吨</Text>
                            </div>
                        )}
                    </div>
                )}

                {/* 新增：机组接送详情 Popover 展示 */}
                {transactionType === TRANSACTION_TYPES.CREW_TRANSPORTATION && currentTransaction.details && (
                    <div className="transaction-details">
                        {currentTransaction.details.route && (
                            <div className="transaction-detail-item">
                                <Text type="secondary" style={{ fontSize: "12px" }}>
                                    路线:{" "}
                                </Text>
                                <Text style={{ fontSize: "12px" }}>{currentTransaction.details.route === "HOTEL_TO_AIRPORT" ? "酒店 - 机场" : currentTransaction.details.route === "AIRPORT_TO_HOTEL" ? "机场 - 酒店" : currentTransaction.details.route}</Text>
                            </div>
                        )}
                        {currentTransaction.details.pickup_time && (
                            <div className="transaction-detail-item">
                                <Text type="secondary" style={{ fontSize: "12px" }}>
                                    接送时间:{" "}
                                </Text>
                                <Text style={{ fontSize: "12px" }}>{dayjs(currentTransaction.details.pickup_time).format("YYYY-MM-DD HH:mm")}</Text>
                            </div>
                        )}
                        {currentTransaction.details.pax_number !== undefined && (
                            <div className="transaction-detail-item">
                                <Text type="secondary" style={{ fontSize: "12px" }}>
                                    人数:{" "}
                                </Text>
                                <Text style={{ fontSize: "12px" }}>{currentTransaction.details.pax_number} 人</Text>
                            </div>
                        )}
                        {currentTransaction.details.version && (
                            <div className="transaction-detail-item">
                                <Text type="secondary" style={{ fontSize: "12px" }}>
                                    版本:{" "}
                                </Text>
                                <Text style={{ fontSize: "12px" }}>{currentTransaction.details.version}</Text>
                            </div>
                        )}
                    </div>
                )}

                {/* 新增：HOTAC详情 Popover 展示 */}
                {transactionType === TRANSACTION_TYPES.HOTAC && currentTransaction.details && (
                    <div className="transaction-details">
                        {currentTransaction.details.pax_number !== undefined && (
                            <div className="transaction-detail-item">
                                <Text type="secondary" style={{ fontSize: "12px" }}>
                                    人数:{" "}
                                </Text>
                                <Text style={{ fontSize: "12px" }}>{currentTransaction.details.pax_number} 人</Text>
                            </div>
                        )}
                        {currentTransaction.details.version && (
                            <div className="transaction-detail-item">
                                <Text type="secondary" style={{ fontSize: "12px" }}>
                                    版本:{" "}
                                </Text>
                                <Text style={{ fontSize: "12px" }}>{currentTransaction.details.version}</Text>
                            </div>
                        )}
                    </div>
                )}

                {currentTransaction.remarks && (
                    <div className="transaction-remarks">
                        <Divider style={{ margin: "8px 0" }} />
                        <Text type="secondary" style={{ fontSize: "12px" }}>
                            备注:
                        </Text>
                        <div className="remarks-content">
                            <Text style={{ fontSize: "12px", whiteSpace: "pre-wrap" }}>{currentTransaction.remarks}</Text>
                        </div>
                    </div>
                )}

                {/* 基本信息 */}
                <div className="transaction-meta">
                    <div className="meta-item">
                        <InfoCircleOutlined style={{ fontSize: "11px", marginRight: "4px" }} />
                        <Text style={{ fontSize: "12px", color: "#888" }}>更新时间: {dayjs(currentTransaction.updated_at || currentTransaction.created_at).format("YYYY-MM-DD HH:mm")}</Text>
                    </div>
                </div>

                <div className="transaction-actions">
                    <Button type="primary" size="small" icon={<EditOutlined />} onClick={handleOpenModal} className="transaction-action-button">
                        编辑
                    </Button>
                    <Button type="default" size="small" icon={<HistoryOutlined />} onClick={handleViewHistory} className="transaction-action-button">
                        历史详情
                    </Button>
                </div>
            </div>
        );
    };

    // 事件历史记录颜色
    const getEventColor = (eventType) => {
        if (eventType.includes("APPROVED") || eventType.includes("CONFIRMED") || eventType.includes("RELEASED")) {
            return "green";
        } else if (eventType.includes("REJECTED") || eventType.includes("CANCELLED")) {
            return "red";
        } else if (eventType.includes("PENDING") || eventType.includes("CREATED")) {
            return "blue";
        }
        return "gray";
    };

    // 渲染完整的事件历史记录（Modal中显示）
    const renderFullEventHistory = () => {
        if (eventLogsLoading) {
            return (
                <div className="event-history-loading">
                    <Spin tip="加载事件历史..." />
                </div>
            );
        }

        if (!eventLogs || eventLogs.length === 0) {
            return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="暂无相关事件记录" className="event-history-empty" />;
        }

        return (
            <Timeline
                className="event-history-timeline"
                items={eventLogs.map((log) => ({
                    color: getEventColor(log.event_type),
                    children: (
                        <div key={log.id} className="event-log-item">
                            <div className="event-log-header">
                                <Text type="secondary" style={{ fontSize: "12px" }}>
                                    {dayjs(log.created_at).format("YYYY-MM-DD HH:mm:ss")}
                                </Text>
                                {log.operator && (
                                    <>
                                        <Divider type="vertical" style={{ height: "16px", margin: "0 10px" }} />
                                        <Text type="secondary" style={{ fontSize: "12px" }}>
                                            操作者: <span style={{ color: "#222" }}>{log.operator}</span>
                                        </Text>
                                    </>
                                )}
                            </div>
                            {log.event_description && (
                                <div className="event-log-description markdown-content">
                                    <ReactMarkdown>{log.event_description}</ReactMarkdown>
                                </div>
                            )}
                            {log.field_name && (
                                <div className="event-log-field-change">
                                    <Text type="secondary">{log.field_name}: </Text>
                                    {log.previous_value && (
                                        <Text delete className="previous-value">
                                            {log.previous_value}
                                        </Text>
                                    )}
                                    {log.previous_value && log.new_value && <span className="change-arrow"> → </span>}
                                    {log.new_value && <Text className="new-value">{log.new_value}</Text>}
                                </div>
                            )}
                        </div>
                    ),
                }))}
            />
        );
    };

    // Popover标题
    const popoverTitle = (
        <div className="transaction-popover-title">
            <span>{TRANSACTION_TYPE_LABELS[transactionType]}</span>
            {currentTransaction && <TransactionStatusTag status={currentTransaction.status} type={transactionType} showIcon size="default" />}
        </div>
    );

    // Tab标签渲染函数 - 修改逻辑，仅在 status 有值时显示状态标签
    const renderTabTitle = (label, status) => (
        <span>
            {label}
            {status && <TransactionStatusTag status={status} textOnly showIcon size="small" style={{ marginLeft: 6 }} />}
        </span>
    );

    // 渲染特定事务类型的表单字段
    const renderTransactionFields = () => {
        switch (transactionType) {
            case TRANSACTION_TYPES.FUEL_REQUEST:
                return (
                    <>
                        <Form.Item label="订单编号" name="orderNumber">
                            <Input placeholder="输入燃油订单编号" onChange={(e) => recordFieldChange("orderNumber", e.target.value)} />
                        </Form.Item>
                        <Form.Item label="有效期" name="expiryDate">
                            <DatePicker placeholder="选择有效期" style={{ width: "100%" }} onChange={(date) => recordFieldChange("expiryDate", date)} />
                        </Form.Item>
                    </>
                );

            case TRANSACTION_TYPES.PERMIT_REQUEST:
                return (
                    <Tabs defaultActiveKey="departureLanding" style={{ marginBottom: 16 }} className="transaction-detail-tabs">
                        <TabPane tab={renderTabTitle("始发机场起降许可", tabStatus.departureLanding)} key="departureLanding">
                            <Form.Item label="状态" name="departureLandingStatus">
                                <TransactionStatusSelector form={form} fieldName="departureLandingStatus" transactionType={transactionType} onFieldChange={recordFieldChange} />
                            </Form.Item>
                            <Form.Item label="参考编号" name="departureLandingReferenceNumber">
                                <Input placeholder="输入参考编号" onChange={(e) => recordFieldChange("departureLandingReferenceNumber", e.target.value)} />
                            </Form.Item>
                            <Form.Item label="舱单编号" name="departureLandingManifestNumber">
                                <Input placeholder="输入舱单编号" onChange={(e) => recordFieldChange("departureLandingManifestNumber", e.target.value)} />
                            </Form.Item>
                        </TabPane>
                        <TabPane tab={renderTabTitle("飞越许可", tabStatus.overflight)} key="overflight">
                            <Form.Item label="状态" name="overFlightStatus">
                                <TransactionStatusSelector form={form} fieldName="overFlightStatus" transactionType={transactionType} onFieldChange={recordFieldChange} />
                            </Form.Item>
                            <Form.Item label="参考编号" name="overFlightReferenceNumber">
                                <Input placeholder="输入参考编号" onChange={(e) => recordFieldChange("overFlightReferenceNumber", e.target.value)} />
                            </Form.Item>
                            <Form.Item label="标准起飞时间" name="overFlightStd">
                                <DatePicker showTime style={{ width: "100%" }} onChange={(date) => recordFieldChange("overFlightStd", date)} />
                            </Form.Item>
                            <Form.Item label="标准到达时间" name="overFlightSta">
                                <DatePicker showTime style={{ width: "100%" }} onChange={(date) => recordFieldChange("overFlightSta", date)} />
                            </Form.Item>
                            <Form.Item label="延误小时数" name="overFlightDelayHours">
                                <InputNumber min={0} max={72} style={{ width: "100%" }} onChange={(value) => recordFieldChange("overFlightDelayHours", value)} />
                            </Form.Item>
                        </TabPane>
                        <TabPane tab={renderTabTitle("目的机场起降许可", tabStatus.arrivalLanding)} key="arrivalLanding">
                            <Form.Item label="状态" name="arrivalLandingStatus">
                                <TransactionStatusSelector form={form} fieldName="arrivalLandingStatus" transactionType={transactionType} onFieldChange={recordFieldChange} />
                            </Form.Item>
                            <Form.Item label="参考编号" name="arrivalLandingReferenceNumber">
                                <Input placeholder="输入参考编号" onChange={(e) => recordFieldChange("arrivalLandingReferenceNumber", e.target.value)} />
                            </Form.Item>
                            <Form.Item label="舱单编号" name="arrivalLandingManifestNumber">
                                <Input placeholder="输入舱单编号" onChange={(e) => recordFieldChange("arrivalLandingManifestNumber", e.target.value)} />
                            </Form.Item>
                        </TabPane>
                    </Tabs>
                );

            case TRANSACTION_TYPES.CATERING_ORDER:
                return (
                    <>
                        <Form.Item label="版本" name="version">
                            <Input placeholder="输入版本号" onChange={(e) => recordFieldChange("version", e.target.value)} />
                        </Form.Item>
                        <Form.Item label="机组人数" name="crewNumber">
                            <InputNumber min={1} max={20} style={{ width: "100%" }} onChange={(value) => recordFieldChange("crewNumber", value)} />
                        </Form.Item>
                        <Form.Item label="订单编号" name="orderNo">
                            <Input placeholder="输入订单编号" onChange={(e) => recordFieldChange("orderNo", e.target.value)} />
                        </Form.Item>
                    </>
                );

            case TRANSACTION_TYPES.FLIGHT_BRIEF:
                return (
                    <>
                        <Form.Item label="最大载荷(吨)" name="maxPayload">
                            <InputNumber min={0} max={1000} step={0.1} style={{ width: "100%" }} onChange={(value) => recordFieldChange("maxPayload", value)} />
                        </Form.Item>
                        <Form.Item label="版本" name="version">
                            <Input placeholder="输入版本号" onChange={(e) => recordFieldChange("version", e.target.value)} />
                        </Form.Item>
                    </>
                );

            case TRANSACTION_TYPES.CREW_TRANSPORTATION:
                return (
                    <>
                        <Form.Item label="路线" name="route">
                            <Select placeholder="选择路线" onChange={(value) => recordFieldChange("route", value)} allowClear>
                                <Select.Option value="HOTEL_TO_AIRPORT">H→A (酒店→机场)</Select.Option>
                                <Select.Option value="AIRPORT_TO_HOTEL">A→H (机场→酒店)</Select.Option>
                            </Select>
                        </Form.Item>
                        <Form.Item label="接送时间 (LT)" name="pickup_time">
                            {/* 不显示秒 */}
                            <DatePicker showTime placeholder="选择接送时间" style={{ width: "100%" }} format="YYYY-MM-DD HH:mm" onChange={(date) => recordFieldChange("pickup_time", date)} getPopupContainer={(trigger) => trigger.parentElement} />
                        </Form.Item>
                        <Form.Item label="人数" name="pax_number">
                            <InputNumber min={1} placeholder="输入人数" style={{ width: "100%" }} onChange={(value) => recordFieldChange("pax_number", value)} />
                        </Form.Item>
                        <Form.Item label="版本" name="version">
                            <Input placeholder="输入版本号" onChange={(e) => recordFieldChange("version", e.target.value)} />
                        </Form.Item>
                    </>
                );

            case TRANSACTION_TYPES.HOTAC:
                return (
                    <>
                        <Form.Item label="人数" name="pax_number">
                            <InputNumber min={1} placeholder="输入人数" style={{ width: "100%" }} onChange={(value) => recordFieldChange("pax_number", value)} />
                        </Form.Item>
                        <Form.Item label="版本" name="version">
                            <Input placeholder="输入版本号" onChange={(e) => recordFieldChange("version", e.target.value)} />
                        </Form.Item>
                    </>
                );

            default:
                return <Empty description="没有可用的表单字段" />;
        }
    };

    // 渲染航班基本信息
    const renderFlightBasicInfo = () => {
        if (!flight) return null;

        // 获取航班状态颜色
        const statusColor = FLIGHT_STATUS_COLORS[flight.status] || "#999";
        // 获取航班状态中文标签
        const statusLabel = FLIGHT_STATUS_LABELS[flight.status] || flight.status; // 如果找不到标签，则fallback到原状态码

        return (
            <div style={{ padding: "8px 0", borderBottom: "1px solid #f0f0f0", marginBottom: "16px" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <Space>
                        <span style={{ fontWeight: "bold" }}>
                            {flight.flight_number} ({flight.departure_airport} → {flight.arrival_airport}, {flight.flight_date ? dayjs.utc(flight.flight_date).format("MM-DD") : "未知"})
                        </span>
                        {flight.stc_type && (
                            <Tag color="cyan" style={{ marginLeft: 4 }}>
                                {flight.stc_type}
                            </Tag>
                        )}
                        {flight.status && (
                            <Tag
                                style={{
                                    color: statusColor,
                                    borderColor: statusColor,
                                    display: "inline-block",
                                }}
                            >
                                {statusLabel}
                            </Tag>
                        )}
                    </Space>
                </div>
            </div>
        );
    };

    // 已修改字段展示组件
    const ModifiedFieldsDisplay = ({ modifiedFields, originalValues, selectedFlights }) => {
        // 获取所有已修改字段的可读标签
        const getFieldLabel = (fieldName) => {
            const fieldLabels = {
                status: "状态",
                remarks: "备注",
                // 始发机场起降许可
                departureLandingStatus: "始发许可-状态",
                departureLandingReferenceNumber: "始发许可-参考编号",
                departureLandingManifestNumber: "始发许可-舱单编号",
                // 目的机场起降许可
                arrivalLandingStatus: "目的许可-状态",
                arrivalLandingReferenceNumber: "目的许可-参考编号",
                arrivalLandingManifestNumber: "目的许可-舱单编号",
                // 飞越许可
                overFlightStatus: "飞越许可-状态",
                overFlightReferenceNumber: "飞越许可-参考编号",
                overFlightStd: "飞越许可-标准起飞时间",
                overFlightSta: "飞越许可-标准到达时间",
                overFlightDelayHours: "飞越许可-延误小时数",
                // 燃油申请
                orderNumber: "订单编号",
                expiryDate: "有效期",
                // 通用
                maxPayload: "最大载荷",
                version: "版本",
                crewNumber: "机组人数",
                orderNo: "订单编号",

                // Crew Transportation 新增字段
                route: "路线 (机组接送)",
                pickup_time: "接送时间 (机组接送)",
                pax_number: "人数", // pax_number 已存在，但增加上下文描述
                // version: "版本", // 已有
                // order_number: "订单编号", // 已有

                // HOTAC 新增字段 (HOTAC的 pax_number, version, order_number 与其他事务共享标签)
            };

            // 为 pax_number, version, order_number 根据 transactionType 提供更具体的标签
            if (transactionType === TRANSACTION_TYPES.CREW_TRANSPORTATION) {
                if (fieldName === "pax_number") return "人数 (机组接送)";
                if (fieldName === "version") return "版本 (机组接送)";
                if (fieldName === "order_number") return "订单号 (机组接送)";
            } else if (transactionType === TRANSACTION_TYPES.HOTAC) {
                if (fieldName === "pax_number") return "人数 (酒店)";
                if (fieldName === "version") return "版本 (酒店)";
                if (fieldName === "order_number") return "订单号 (酒店)";
            } else if (transactionType === TRANSACTION_TYPES.CATERING_ORDER) {
                if (fieldName === "orderNo") return "订单号 (配餐)"; // Catering 使用 orderNo
            }

            return fieldLabels[fieldName] || fieldName;
        };

        // 格式化字段值以便显示
        const formatFieldValue = (fieldName, value) => {
            if (value === undefined || value === null) {
                return "无";
            }

            // 处理日期类型
            if (fieldName.includes("Std") || fieldName.includes("Sta") || fieldName === "expiryDate") {
                if (typeof value === "object" && value.format) {
                    return value.format("YYYY-MM-DD HH:mm");
                } else if (typeof value === "string") {
                    return value;
                }
            }

            // 处理pickup_time字段 - 本地时间，不进行时区转换
            if (fieldName === "pickup_time") {
                if (typeof value === "object" && value.format) {
                    return value.format("YYYY-MM-DD HH:mm");
                } else if (typeof value === "string") {
                    return value;
                }
            }

            // 处理状态类型
            if (fieldName.includes("Status") || fieldName === "status") {
                return TRANSACTION_STATUS_LABELS[value] || value;
            }

            // 最大显示10个字符
            if (String(value).length > 10) {
                return String(value).slice(0, 10) + "...";
            }
            return String(value);
        };

        return (
            <div style={{ marginBottom: 16 }}>
                <Alert
                    message={
                        <>
                            将对 <strong>{selectedFlights.length > 0 ? `${selectedFlights[0].flight_number} 航班` : "当前航班"}</strong> 更新以下信息
                        </>
                    }
                    description={
                        <div>
                            {Object.keys(modifiedFields).map((fieldName) => (
                                <Tooltip
                                    key={fieldName}
                                    title={
                                        <div>
                                            <div>
                                                <strong>字段名称:</strong> {getFieldLabel(fieldName)}
                                            </div>
                                            <div>
                                                <strong>原始值:</strong> {formatFieldValue(fieldName, originalValues[fieldName])}
                                            </div>
                                            <div>
                                                <strong>修改后的值:</strong> {formatFieldValue(fieldName, modifiedFields[fieldName])}
                                            </div>
                                        </div>
                                    }
                                    placement="top"
                                >
                                    <Tag color="blue" style={{ margin: "4px", cursor: "pointer" }}>
                                        <EditOutlined /> {getFieldLabel(fieldName)}: {formatFieldValue(fieldName, originalValues[fieldName])} → {formatFieldValue(fieldName, modifiedFields[fieldName])}
                                    </Tag>
                                </Tooltip>
                            ))}
                        </div>
                    }
                    type="info"
                    showIcon
                />
            </div>
        );
    };

    return (
        <>
            {/* 添加一个隐藏的Form组件，确保useForm创建的实例始终与Form元素关联 */}
            <Form form={form} style={{ display: "none" }}>
                <Form.Item name="hidden">
                    <Input />
                </Form.Item>
            </Form>

            <Popover content={renderPopoverContent()} title={popoverTitle} trigger={trigger} open={visible} onOpenChange={onVisibleChange} placement={placement} destroyOnHidden={true} overlayClassName="transaction-popover">
                {children}
            </Popover>

            {/* 编辑Modal */}
            <Modal
                title={
                    <div className="transaction-modal-title">
                        {currentTransaction ? "编辑" : "创建"}
                        {TRANSACTION_TYPE_LABELS[transactionType]}
                        {currentTransaction && <TransactionStatusTag status={currentTransaction.status} type={transactionType} showIcon size="default" />}
                    </div>
                }
                open={modalVisible}
                onCancel={() => setModalVisible(false)}
                width={1000}
                className="transaction-modal transaction-modal-wide"
                destroyOnHidden={true}
                footer={[
                    <Button key="cancel" onClick={() => setModalVisible(false)}>
                        取消
                    </Button>,
                    <Button key="submit" type="primary" onClick={handleSubmit} loading={isSubmitting} disabled={Object.keys(modifiedFields).length === 0 || currentTransaction?.migration_status === TRANSACTION_MIGRATION_STATUS.PENDING_REVIEW}>
                        保存
                    </Button>,
                ]}
            >
                {/* 添加航班基本信息 */}
                {renderFlightBasicInfo()}

                {/* 左右布局：左侧表单，右侧历史记录和溯源 */}
                <div className="transaction-modal-content" style={{ display: "flex", gap: "24px", height: "500px" }}>
                    {/* 左侧：事务详情表单 */}
                    <div className="transaction-form-section" style={{ flex: "1", minWidth: "400px" }}>
                        <div className="section-title" style={{ marginBottom: "16px", fontSize: "16px", fontWeight: "600", borderBottom: "1px solid #f0f0f0", paddingBottom: "8px" }}>
                            <EditOutlined style={{ marginRight: "8px", color: "#1890ff" }} />
                            {currentTransaction?.migration_status === TRANSACTION_MIGRATION_STATUS.REJECTED ? "新增事务" : "事务详情"}
                        </div>
                        <div style={{ overflowY: "auto", height: "calc(100% - 40px)" }}>
                            {/* 如果是待检查状态，显示只读提示 */}
                            {currentTransaction?.migration_status === TRANSACTION_MIGRATION_STATUS.PENDING_REVIEW ? <Alert message="事务待检查确认" description="此事务正在等待迁移确认，暂时无法编辑。请先在右侧确认或拒绝迁移状态。" type="warning" showIcon style={{ marginBottom: "16px" }} /> : null}

                            <Form form={form} layout="vertical" initialValues={formInitialValues} className="transaction-form" disabled={currentTransaction?.migration_status === TRANSACTION_MIGRATION_STATUS.PENDING_REVIEW}>
                                {/* 显示已修改的字段 */}
                                {Object.keys(modifiedFields).length > 0 && <ModifiedFieldsDisplay modifiedFields={modifiedFields} originalValues={formInitialValues} selectedFlights={flight ? [flight] : []} />}

                                {/* 当事务类型不是许可申请时，才显示主状态 */}
                                {transactionType !== TRANSACTION_TYPES.PERMIT_REQUEST && (
                                    <Form.Item label="状态" name="status">
                                        <TransactionStatusSelector form={form} fieldName="status" transactionType={transactionType} onFieldChange={recordFieldChange} />
                                    </Form.Item>
                                )}
                                <Form.Item label="备注" name="remarks">
                                    <TextArea rows={3} placeholder="输入备注信息" onChange={(e) => recordFieldChange("remarks", e.target.value)} />
                                </Form.Item>
                                {/* 根据事务类型渲染不同的表单字段 */}
                                {renderTransactionFields()}
                            </Form>
                        </div>
                    </div>

                    {/* 右侧：历史记录和事务溯源 */}
                    <div className="transaction-history-section" style={{ flex: "1", minWidth: "400px", borderLeft: "1px solid #f0f0f0", paddingLeft: "24px" }}>
                        {!currentTransaction ? (
                            <Alert message="请先保存事务" description="保存事务后可查看历史记录和溯源信息" type="info" showIcon style={{ marginTop: "20px" }} />
                        ) : (
                            <div className="history-content" style={{ overflowY: "auto", height: "calc(100% - 40px)" }}>
                                {/* 事务溯源部分 */}
                                <div className="traceability-section" style={{ marginBottom: "24px" }}>
                                    <TransactionHistoryChain
                                        transactionType={transactionType}
                                        transactionId={currentTransaction?.id}
                                        flightId={flightId}
                                        visible={modalVisible}
                                        onRefresh={() => {
                                            // 刷新当前事务数据
                                            if (onSuccess) onSuccess(flightId);
                                            // 重新获取事务数据
                                            if (fetchTransactions) fetchTransactions();
                                        }}
                                        onRejectAndClose={() => {
                                            // 拒绝后关闭Modal并刷新数据
                                            setModalVisible(false);
                                            if (onSuccess) onSuccess(flightId);
                                            if (fetchTransactions) fetchTransactions();
                                        }}
                                    />
                                </div>

                                {/* 历史记录部分 */}
                                <div className="event-logs-section">
                                    <div className="subsection-title" style={{ marginBottom: "12px", fontSize: "14px", fontWeight: "500", color: "#666", borderBottom: "1px dashed #e8e8e8", paddingBottom: "6px" }}>
                                        <HistoryOutlined style={{ marginRight: "6px" }} />
                                        操作历史
                                    </div>
                                    {renderFullEventHistory()}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </Modal>
        </>
    );
};

export default TransactionEditPopover;
